// ==================== USE SUB-ACCOUNTS HOOK ====================
// List + delete / activate / deactivate state for one organization's sub-accounts.
// Shared by the customer's Sub-accounts page and the admin's customer modal.

import { useState, useEffect, useCallback } from 'react';
import type { SubAccount, SubAccountsApi } from '../services/sub-accounts.service';

export function useSubAccounts(api: SubAccountsApi) {
  // State
  const [subAccounts, setSubAccounts] = useState<SubAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Delete confirmation
  const [deleteConfirm, setDeleteConfirm] = useState<SubAccount | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Status toggle confirmation
  const [toggleConfirm, setToggleConfirm] = useState<SubAccount | null>(null);
  const [toggling, setToggling] = useState(false);

  // ==================== FETCH SUB-ACCOUNTS ====================

  const fetchSubAccounts = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.getAll();
      setSubAccounts(response || []);
    } catch (err) {
      console.error('Failed to fetch sub-accounts:', err);
      setError('Failed to load sub-accounts');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchSubAccounts();
  }, [fetchSubAccounts]);

  // ==================== HANDLERS ====================

  const showSuccess = useCallback((message: string) => {
    setSuccess(message);

    // Clear success message after 3 seconds
    setTimeout(() => setSuccess(null), 3000);
  }, []);

  const handleDelete = async () => {
    if (!deleteConfirm) return;

    try {
      setDeleting(true);
      await api.delete(deleteConfirm.id);
      showSuccess('Sub-account deleted successfully');
      setDeleteConfirm(null);
      fetchSubAccounts();
    } catch (err: unknown) {
      console.error('Failed to delete sub-account:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete sub-account';
      setError(errorMessage);
      setDeleteConfirm(null);
    } finally {
      setDeleting(false);
    }
  };

  const confirmToggleStatus = async () => {
    if (!toggleConfirm) return;

    try {
      setToggling(true);
      const isActive = !toggleConfirm.isActive;
      await api.update(toggleConfirm.id, { isActive });
      showSuccess(`Sub-account ${isActive ? 'activated' : 'deactivated'} successfully`);
      setToggleConfirm(null);
      fetchSubAccounts();
    } catch (err) {
      console.error('Failed to toggle status:', err);
      setError('Failed to update sub-account status');
    } finally {
      setToggling(false);
    }
  };

  return {
    subAccounts,
    loading,
    error,
    setError,
    success,
    setSuccess,
    showSuccess,
    fetchSubAccounts,
    deleteConfirm,
    setDeleteConfirm,
    deleting,
    handleDelete,
    toggleConfirm,
    setToggleConfirm,
    toggling,
    confirmToggleStatus,
  };
}

export default useSubAccounts;
